import { useState, useEffect, createContext } from "react";
import { useNavigate } from "react-router-dom";
import jwt_decode from "jwt-decode";
import { useAlert } from "./AlertContextHelper";
import { apiBaseurl } from "../utils/ApiBaseurl";
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authTokens, setAuthTokens] = useState(() =>
    localStorage.getItem("authTokens")
      ? JSON.parse(localStorage.getItem("authTokens"))
      : null
  );
  const [user, setUser] = useState(() =>
    localStorage.getItem("authTokens")
      ? jwt_decode(JSON.parse(localStorage.getItem("authTokens")).access)
      : null
  );
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { setAlertMessage, setAlertColor } = useAlert();

  const forgotPasswordRequest = async (email) => {
    const response = await fetch(
      `${apiBaseurl}backend/api/password-reset-request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
        }),
      }
    );
    const details = await response.json();
    if (response.status === 200){
      setAlertColor("success");
      setAlertMessage(details.message);
      navigate("/");
    } else {
      setAlertColor("failure");
      setAlertMessage(details.message);
    }
  };

  const resetPassword = async (token, password, confirmPassword) => {
    if (password !== confirmPassword) {
      setAlertColor("failure");
      setAlertMessage("Password not matched. Kindly correct it.");
    } else {
      const response = await fetch(
        `${apiBaseurl}backend/api/password-reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            new_password: password,
            token: token,
          }),
        }
      );
      const details = await response.json();
      if (response.status === 200) {
        setAlertColor("success");
        setAlertMessage(details.message);
        navigate("/");
      } else {
        setAlertColor("failure");
        setAlertMessage(details.message);
      }
    }
  };

  const loginUser = async (username, password) => {
    const response = await fetch(`${apiBaseurl}backend/api/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: username,
        password: password,
      }),
    });

    const tokens = await response.json();
    if (response.status === 200) {
      setAuthTokens(tokens);
      setUser(jwt_decode(tokens.access));
      localStorage.setItem("authTokens", JSON.stringify(tokens));
      navigate("/");
    } else {
      if (response.statusText === "Unauthorized") {
        setAlertMessage(
          "unauthorized user. please check your username and password."
        );
        setAlertColor("failure");
      } else {
        setAlertMessage("something went wrong. please try again.");
        setAlertColor("failure");
      }
    }
  };

  const logoutUser = () => {
    setAuthTokens(null);
    setUser(null);
    localStorage.removeItem("authTokens");
    navigate("/login");
  };

  const updateToken = async () => {
    if (authTokens) {
      const response = await fetch(`${apiBaseurl}backend/api/token/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: authTokens.refresh }),
      });

      const tokens = await response.json();
      if (response.status === 200) {
        setAuthTokens(tokens);
        setUser(jwt_decode(tokens.access));
        localStorage.setItem("authTokens", JSON.stringify(tokens));
      } else {
        logoutUser();
      }
    }

    if (loading) {
      setLoading(false);
    }
  };

  const contextData = {
    user: user,
    loginUser: loginUser,
    logoutUser: logoutUser,
    forgotPasswordRequest: forgotPasswordRequest,
    resetPassword: resetPassword,
  };

  useEffect(() => {
    if (loading) {
      updateToken();
    }

    const fourMinutes = 1000 * 60 * 4;
    const interval = setInterval(() => {
      if (authTokens) {
        updateToken();
      }
    }, fourMinutes);
    return () => clearInterval(interval);
  }, [authTokens, loading]);

  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};
